import React, { useState } from 'react'
import {
  Box,
  CircularProgress,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import { Theme, useTheme } from '@mui/material/styles'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Info } from '@mui/icons-material'

export type NotificationIconStatus = 'active' | 'inactive' | 'needsSetup' | 'warning'

export interface NotificationIconAction {
  icon: React.ReactElement
  label: string
  onClick: () => void
  disabled?: boolean
}

interface SimpleNotificationIconProps {
  icon: React.ReactElement
  label: string
  status: NotificationIconStatus
  compact?: boolean
}

const MENU_OFFSET = 8

const getStatusBadge = (status: NotificationIconStatus, theme: Theme, size: number) => {
  const common = {
    fontSize: size,
    position: 'absolute' as const,
    top: -size / 2,
    right: -size / 2,
    backgroundColor: theme.palette.background.custom3 || '#121212',
    borderRadius: '50%',
  }
  if (status === 'active')
    return <CheckCircleIcon sx={{ ...common, color: theme.palette.success.main }} />
  if (status === 'needsSetup') return <Info sx={{ ...common, color: theme.palette.info.main }} />
  return null
}

// Read-only notification icon: status badge, no menu
export const SimpleNotificationIcon: React.FC<SimpleNotificationIconProps> = ({
  icon,
  label,
  status,
  compact = false,
}) => {
  const theme = useTheme()
  const iconColor = status === 'active' ? theme.palette.text.primary : theme.palette.text.disabled
  const iconSize = compact ? 18 : 24

  const body = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: compact ? 'auto' : '25%',
      }}
    >
      <Box sx={{ position: 'relative', display: 'flex' }}>
        {React.cloneElement(icon, { sx: { color: iconColor, fontSize: iconSize } })}
        {getStatusBadge(status === 'warning' ? 'inactive' : status, theme, compact ? 12 : 16)}
      </Box>
      {!compact && (
        <Typography sx={{ color: iconColor, fontSize: '12px', mt: 0.5, textAlign: 'center' }}>
          {label}
        </Typography>
      )}
    </Box>
  )

  return compact ? <Tooltip title={label}>{body}</Tooltip> : body
}

interface NotificationIconWithMenuProps {
  icon: React.ReactElement
  label: string
  status: NotificationIconStatus
  actions: NotificationIconAction[]
  disabled?: boolean
  readOnly?: boolean
  isLoading?: boolean
  // Compact renders icon-only (for the card header) with the menu opening below it
  compact?: boolean
}

// Notification icon with a hover menu of actions (activate, verify phone, connect account, …)
export const NotificationIconWithMenu: React.FC<NotificationIconWithMenuProps> = ({
  icon,
  label,
  status,
  actions,
  disabled = false,
  readOnly = false,
  isLoading = false,
  compact = false,
}) => {
  const theme = useTheme()
  const [isHovered, setIsHovered] = useState(false)
  const [showMenu, setShowMenu] = useState(false)

  const iconSize = compact ? 18 : 24
  const iconColor =
    status === 'active'
      ? theme.palette.text.primary
      : isHovered && !disabled
        ? theme.palette.text.primary
        : theme.palette.text.disabled

  const handleMouseEnter = () => {
    if (!disabled && !readOnly) {
      setIsHovered(true)
      setShowMenu(true)
    }
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    setShowMenu(false)
  }

  const handleActionClick = (actionOnClick: () => void) => {
    actionOnClick()
    setShowMenu(false)
  }

  // Menus render position: fixed so they escape the card; place them relative to the icon
  const positionMenu = (el: HTMLDivElement | null) => {
    if (!el || !showMenu) return
    const iconElement = el.parentElement?.querySelector('[data-icon-container]')
    if (!iconElement) return

    const rect = iconElement.getBoundingClientRect()
    const menuRect = el.getBoundingClientRect()

    let top = compact ? rect.bottom + MENU_OFFSET : rect.top - menuRect.height - MENU_OFFSET
    let left = rect.left + rect.width / 2 - menuRect.width / 2

    if (left < MENU_OFFSET) left = MENU_OFFSET
    if (left + menuRect.width > window.innerWidth - MENU_OFFSET) {
      left = window.innerWidth - menuRect.width - MENU_OFFSET
    }
    if (top < MENU_OFFSET) top = rect.bottom + MENU_OFFSET
    if (top + menuRect.height > window.innerHeight - MENU_OFFSET) {
      top = rect.top - menuRect.height - MENU_OFFSET
    }

    el.style.top = `${top}px`
    el.style.left = `${left}px`
  }

  if (readOnly) {
    return <SimpleNotificationIcon icon={icon} label={label} status={status} compact={compact} />
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: compact ? 'auto' : '25%',
        cursor: disabled ? 'default' : 'pointer',
        overflow: 'visible',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box sx={{ position: 'relative', display: 'flex' }} data-icon-container>
        {React.cloneElement(icon, { sx: { color: iconColor, fontSize: iconSize } })}
        {isLoading && (
          <CircularProgress size={12} sx={{ position: 'absolute', top: -6, right: -6 }} />
        )}
        {getStatusBadge(status, theme, compact ? 12 : 16)}
      </Box>
      {!compact && (
        <Typography
          sx={{
            color: iconColor,
            fontSize: '12px',
            mt: 0.5,
            textAlign: 'center',
            transition: 'color 0.2s ease',
          }}
        >
          {label}
        </Typography>
      )}

      <Fade in={showMenu} timeout={200}>
        <Paper
          ref={positionMenu}
          sx={{
            position: 'fixed',
            py: 0,
            minWidth: 220,
            maxWidth: 280,
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.3)',
            backgroundColor: theme.palette.background.custom3 || '#121212',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            zIndex: 9999,
          }}
        >
          <List dense sx={{ py: 1 }}>
            {compact && (
              <Typography
                sx={{
                  px: 2,
                  pb: 0.5,
                  fontSize: '11px',
                  color: theme.palette.text.secondary,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {label}
              </Typography>
            )}
            {actions.map((action, index) => (
              <ListItem
                component={'button'}
                key={index}
                onClick={() => handleActionClick(action.onClick)}
                disabled={action.disabled}
                sx={{
                  py: 1,
                  px: 2,
                  minHeight: 40,
                  cursor: action.disabled ? 'not-allowed' : 'pointer',
                  backgroundColor: 'transparent',
                  border: 'none',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderRadius: 0,
                  color: action.disabled ? theme.palette.text.disabled : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: action.disabled
                      ? 'transparent'
                      : theme.palette.background.custom4 || '#1e1e1e',
                  },
                  '&:focus': {
                    outline: 'none',
                    backgroundColor: theme.palette.background.custom4 || '#1e1e1e',
                  },
                  '&:not(:last-child)': {
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 28,
                    color: action.disabled
                      ? theme.palette.text.disabled
                      : theme.palette.primary.main,
                  }}
                >
                  {React.cloneElement(action.icon, { fontSize: 'small' })}
                </ListItemIcon>
                <ListItemText
                  primary={action.label}
                  primaryTypographyProps={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: action.disabled
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary,
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Fade>
    </Box>
  )
}
